// lib/store/slices/chatbotSlice.ts
import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import type { ChatbotState, ChatSession } from '@/types';

const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3000/portfolio/api/v1';

const initialState: ChatbotState = {
    sessionId: null,
    lang: null,
    messages: [],
    streamingContent: '',
    isStreaming: false,
    suggestions: [],
    currentSession: null,
    loading: false,
    error: null,
};

// ── GET /chatbot/sessions/:sessionId (public) ─────────────────────────────────

export const fetchSessionById = createAsyncThunk(
    'chatbot/fetchSessionById',
    async (sessionId: string, { rejectWithValue }) => {
        try {
            const response = await fetch(`${API_BASE_URL}/chatbot/sessions/${sessionId}`);
            if (!response.ok) throw new Error('Session not found');
            const json = await response.json();
            return json.data as ChatSession;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to fetch session');
        }
    }
);

// ── POST /chatbot/message (SSE streaming, public) ─────────────────────────────

export const sendChatMessage = createAsyncThunk(
    'chatbot/sendMessage',
    async (
        payload: { sessionId?: string; userMessage: string; lang?: string; userId?: string },
        { dispatch, rejectWithValue }
    ) => {
        dispatch(startStreaming());

        try {
            const response = await fetch(`${API_BASE_URL}/chatbot/message`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            if (!response.ok || !response.body) {
                throw new Error('Failed to connect to chatbot');
            }

            const reader   = response.body.getReader();
            const decoder  = new TextDecoder();
            let buffer     = '';
            let fullContent = '';
            let suggestions: string[] = [];
            let finalSessionId: string | null = payload.sessionId ?? null;
            let finalLang: string | null = payload.lang ?? null;

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');
                buffer = lines.pop() ?? '';

                for (const line of lines) {
                    if (!line.startsWith('data: ')) continue;
                    try {
                        const event = JSON.parse(line.slice(6));
                        switch (event.type) {
                            case 'session':
                                finalSessionId = event.sessionId;
                                finalLang      = event.lang;
                                dispatch(setSession({ sessionId: event.sessionId, lang: event.lang }));
                                break;
                            case 'delta':
                                fullContent += event.text;
                                dispatch(appendStreamDelta(event.text));
                                break;
                            case 'suggestions':
                                suggestions = event.items ?? [];
                                break;
                            case 'error':
                                throw new Error(event.message ?? 'Chatbot error');
                            case 'done':
                            case 'tool_call':
                                break;
                        }
                    } catch {
                        // skip malformed SSE lines
                    }
                }
            }

            return { sessionId: finalSessionId, lang: finalLang, content: fullContent, suggestions };
        } catch (error: any) {
            dispatch(stopStreaming());
            return rejectWithValue(error.message || 'Chat failed');
        }
    }
);

// ── Slice ─────────────────────────────────────────────────────────────────────

const chatbotSlice = createSlice({
    name: 'chatbot',
    initialState,
    reducers: {
        clearError: (state) => { state.error = null; },

        setSession: (state, action: PayloadAction<{ sessionId: string; lang: string }>) => {
            state.sessionId = action.payload.sessionId;
            state.lang      = action.payload.lang;
        },

        addUserMessage: (state, action: PayloadAction<string>) => {
            state.messages.push({
                role: 'user',
                content: action.payload,
                createdAt: new Date().toISOString(),
            });
        },

        startStreaming: (state) => {
            state.isStreaming     = true;
            state.streamingContent = '';
            state.error           = null;
        },

        stopStreaming: (state) => {
            state.isStreaming = false;
        },

        appendStreamDelta: (state, action: PayloadAction<string>) => {
            state.streamingContent += action.payload;
        },

        clearSession: (state) => {
            state.sessionId       = null;
            state.lang            = null;
            state.messages        = [];
            state.streamingContent = '';
            state.isStreaming     = false;
            state.suggestions     = [];
            state.currentSession  = null;
        },
    },

    extraReducers: (builder) => {

        // fetchSessionById
        builder
            .addCase(fetchSessionById.pending, (state) => { state.loading = true; state.error = null; })
            .addCase(fetchSessionById.fulfilled, (state, { payload }) => {
                state.loading        = false;
                state.currentSession = payload;
                state.sessionId      = payload.sessionId;
                state.lang           = payload.lang ?? null;
                state.messages       = payload.messages;
            })
            .addCase(fetchSessionById.rejected, (state, { payload }) => {
                state.loading = false;
                state.error   = payload as string;
            });

        // sendChatMessage
        builder
            .addCase(sendChatMessage.fulfilled, (state, { payload }) => {
                state.isStreaming = false;
                if (payload.content) {
                    state.messages.push({
                        role:      'assistant',
                        content:   payload.content,
                        createdAt: new Date().toISOString(),
                    });
                }
                state.streamingContent = '';
                state.suggestions      = payload.suggestions;
            })
            .addCase(sendChatMessage.rejected, (state, { payload }) => {
                state.isStreaming      = false;
                state.streamingContent = '';
                state.error            = payload as string;
            });
    },
});

export const {
    clearError,
    setSession,
    addUserMessage,
    startStreaming,
    stopStreaming,
    appendStreamDelta,
    clearSession,
} = chatbotSlice.actions;

export default chatbotSlice.reducer;
